import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { MasterService } from '../../../base-classes/master-service';
import { Scenario, Package } from '../models';

@Injectable({
  providedIn: 'root'
})
export class ScenarioService extends MasterService<Scenario> {

  constructor(http: HttpClient) {
    const baseUrl = `${environment.apiUrl}/scenarios`;
    super(http, baseUrl);
  }

  public getClientsAll() {
    return this.http.get<any[]>(`${environment.apiUrl}/clients/userclients`);
  }

  // Get user-specific clients
  public getUserClients() {
    return this.http.get<any[]>(`${environment.apiUrl}/clients/user-clients`);
  }

  // Get user-specific functional areas
  public getUserFunctionalAreas() {
    return this.http.get<any[]>(`${environment.apiUrl}/functional-areas/user-functional-areas`);
  }

  // Referencedata get api
  getReferenceList(codeKeys: string[]): Observable<any[]> {
    return this.http.post<any[]>(`${environment.apiUrl}/reference-values/by-code-keys`, { codeKeys });
  }

  getRecordings() {
    return this.http.get<any[]>(`${environment.apiUrl}/recordings`);
  }

  getRecordingById(id: string) {
    return this.http.get<any>(`${environment.apiUrl}/recordings/${id}`);
  }

  /**
   * Get all active packages for dropdown
   */
  getAllPackages(): Observable<Package[]> {
    return this.http.get<Package[]>(`${environment.apiUrl}/packages`);
  }

  /**
   * Get scenarios by package ID
   */
  getScenariosByPackage(packageId: string): Observable<Scenario[]> {
    return this.http.get<Scenario[]>(`${this.baseUrl}/package/${packageId}`);
  }

  /** added loader bypass */
  getScenariosByfunctionalAreaId(functionalAreaId: string): Observable<Scenario[]> {
    return this.http.get<Scenario[]>(`${this.baseUrl}/functional-scenarios/${functionalAreaId}`);
  }

  getDefaultScenarios(): Observable<Scenario[]> {
    return this.http.get<Scenario[]>(`${this.baseUrl}/templates`);
  }

  getScenariosByClientId(clientId: string): Observable<Scenario[]> {
    return this.http.get<Scenario[]>(`${this.baseUrl}/by-client/${clientId}`);
  }

  getScenarioCountsByFunctionalArea(clientId: string): Observable<{ counts: { functionalAreaId: string; functionalAreaName: string; count: number }[]; total: number }> {
    return this.http.get<{ counts: { functionalAreaId: string; functionalAreaName: string; count: number }[]; total: number }>(
      `${this.baseUrl}/counts-by-fa/${clientId}`
    );
  }

  getScenariosPaginated(clientId: string, params: {
    page: number;
    pageSize: number;
    search?: string;
    functionalArea?: string;
    testType?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Observable<{ data: Scenario[]; total: number; page: number; pageSize: number }> {
    const queryParams: any = {
      page: params.page.toString(),
      pageSize: params.pageSize.toString(),
    };
    if (params.search) queryParams.search = params.search;
    if (params.functionalArea) queryParams.functionalArea = params.functionalArea;
    if (params.testType) queryParams.testType = params.testType;
    if (params.sortBy) queryParams.sortBy = params.sortBy;
    if (params.sortOrder) queryParams.sortOrder = params.sortOrder;

    const url = clientId === 'default'
      ? `${this.baseUrl}/templates`
      : `${this.baseUrl}/by-client/${clientId}`;

    return this.http.get<{ data: Scenario[]; total: number; page: number; pageSize: number }>(
      url, { params: queryParams }
    );
  }
  /**
   * Get scenario by ID with full package relationship
   */
  getScenarioById(id: string): Observable<Scenario> {
    return this.http.get<Scenario>(`${this.baseUrl}/${id}`);
  }

  /**
   * Create new scenario
   */
  getScenarioByRecordingId(recordingId: string): Observable<Scenario> {
    return this.http.get<Scenario>(`${this.baseUrl}/by-recording/${recordingId}`);
  }

  createScenario(scenarioData: any): Observable<Scenario> {
    return this.http.post<Scenario>(this.baseUrl, scenarioData);
  }

  /**
   * Update existing scenario
   */
  updateScenario(id: string, scenarioData: any): Observable<Scenario> {
    return this.http.put<Scenario>(`${this.baseUrl}/${id}`, scenarioData);
  }

  /**
   * Delete scenario (soft delete - sets isActive to false)
   */
  deleteScenario(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  /**
   * Activate scenario
   */
  activateScenario(id: string): Observable<Scenario> {
    return this.http.put<Scenario>(`${this.baseUrl}/${id}/activate`, {});
  }

  /**
   * Get scenarios by status (active/inactive)
   */
  getScenariosByStatus(isActive: boolean): Observable<Scenario[]> {
    return this.http.get<Scenario[]>(`${this.baseUrl}/status/${isActive}`);
  }

  getStepsAssociatedScenario(packageId: string): Observable<Scenario> {
    return this.http.get<Scenario>(`${environment.apiUrl}/scenarios/with-steps/package/${packageId}`);
  }

  /**
   * Get steps for a specific scenario
   */
  getScenarioSteps(scenarioId: string): Observable<any[]> {
    return this.http.get<any[]>(`${environment.apiUrl}/steps/scenario/${scenarioId}`);
  }
}